import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';
import userService from '../services/userService';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from './Breadcrumb';
import { Form, Switch } from 'antd';

const EditUserLayer = ({ userId: propUserId }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    is_active: true
  });
  
  // Use userId from props or from URL params
  const userId = propUserId || params.id;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const user = await userService.getUser(userId);
        setFormData({
          email: user.email || '',
          full_name: user.full_name || '',
          password: user.password || '',
          role: user.role || 'user',
          is_active: user.is_active !== undefined ? user.is_active : true,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load user data');
        navigate('/admin/users');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [userId, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare the data to send
      const dataToSend = { ...formData };
      
      // Ensure role is lowercase
      if (dataToSend.role) {
        dataToSend.role = dataToSend.role.toLowerCase();
      }
      
      // Remove password field if it's empty
      if (dataToSend.password === '') {
        delete dataToSend.password;
      }
      
      await userService.updateUser(userId, dataToSend);
      toast.success('User updated successfully');
      navigate('/admin/users');
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update user';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.email) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb 
        title="Edit User" 
        items={[
          { title: 'Users', link: '/admin/users' },
          { title: 'Edit User', link: '' }
        ]} 
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <div className="header-title">
                  <h4 className="card-title">Edit User Information</h4>
                </div>
              </div>
              <div className="card-body">

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                                      
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Full Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          name="password"
                          placeholder="Leave blank to keep current password"
                          value={formData.password}
                          onChange={handleChange}
                          minLength={6}
                        />
                        <small className="form-text text-muted">
                          Leave blank to keep current password
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Role</label>
                        <select
                          className="form-select"
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          required
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-check form-switch mb-3">
                        {/* <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="is_active"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                        /> */}
                        <Form.Item 
                          name="is_active"
                          className='form-check-input'
                          valuePropName="checked"
                          noStyle
                          id="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                          rules={[
                            //{ required: true, message: 'This field is required' },
                            { type: 'boolean' }
                          ]}
                        >
                          <Switch 
                            checkedChildren="On" 
                            unCheckedChildren="Off"
                          />
                        </Form.Item>
                        <label className="form-check-label ms-3" htmlFor="is_active">
                          Active
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => navigate('/admin/users')}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          <>
                            <Icon icon="ic:baseline-save" className="icon" />
                            Update User
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
                
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default EditUserLayer;
